/**
 * PushNotificationToggle – small UI control for enabling/disabling push notifications.
 *
 * Drop this into a settings page or notification preferences panel.
 *
 * Usage:
 *   import PushNotificationToggle from '@/components/notifications/PushNotificationToggle';
 *   <PushNotificationToggle />
 */

import React from 'react';
import usePushNotifications from '../../hooks/usePushNotifications';

export default function PushNotificationToggle() {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
  } = usePushNotifications();

  if (!isSupported) {
    return (
      <div className="text-sm text-gray-400">
        Push notifications are not supported in this browser.
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="text-sm text-red-400">
        Notifications are blocked. Please enable them in your browser settings.
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <button
        onClick={isSubscribed ? unsubscribe : subscribe}
        disabled={loading}
        className={`
          relative inline-flex h-6 w-11 items-center rounded-full 
          transition-colors duration-200 ease-in-out focus:outline-none 
          focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900
          ${isSubscribed ? 'bg-blue-600' : 'bg-gray-600'}
          ${loading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
        `}
        role="switch"
        aria-checked={isSubscribed}
        aria-label="Push notifications"
      >
        <span
          className={`
            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
            ${isSubscribed ? 'translate-x-6' : 'translate-x-1'}
          `}
        />
      </button>
      <span className="text-sm text-gray-200">
        {loading
          ? 'Updating…'
          : isSubscribed
            ? 'Push notifications enabled'
            : 'Enable push notifications'}
      </span>
      {error && <span className="text-xs text-red-400 ml-2">{error}</span>}
    </div>
  );
}
